import React, { useState, useEffect } from 'react';
import { 
  Dog, 
  Calendar, 
  Search, 
  Filter,
  Edit
} from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS, getAuthHeaders } from '../config/api.ts';
import ProtectedData from '../components/ProtectedData.tsx';
import FormSelect from '../components/FormSelect.tsx';

interface CheckinInfo {
  id: string;
  checkInTime: string;
  lastUpdateTime?: string;
  branch: {
    _id: string;
    name: string;
    code: string;
  };
  staff: {
    _id: string;
    name: string;
    email: string;
    phone: string;
    position: string;
  };
}

interface Owner {
  _id: string;
  name: string;
  surname: string;
  email: string;
  phone: string;
}

interface Kopek {
  _id: string;
  name: string;
  breed: string;
  gender: string;
  birthDate: string;
  age?: string; // Backend'den gelen hesaplanmış yaş
  weight?: number;
  size: string;
  color: string;
  profileImage?: string;
  description?: string;
  isSpayed?: boolean; // Kısırlık durumu
  owner: Owner;
  isActive: boolean;
  ownerNotes?: string; // Sahip notları
  staffNotes?: string; // Staff notları
  checkinInfo?: CheckinInfo;
}

const OteldekiKopekler: React.FC = () => {
  const [kopekler, setKopekler] = useState<Kopek[]>([]);
  const [todayDogsCount, setTodayDogsCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBreed, setFilterBreed] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmData, setConfirmData] = useState<{
    kopekId: string;
    checkinId: string;
    kopekName: string;
  } | null>(null);
  
  // Düzenle modal state'leri
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editData, setEditData] = useState<{
    kopekId: string;
    checkinId: string;
    kopekName: string;
    staffNotes: string;
    ownerNotes: string;
    selectedCameras: string[];
  } | null>(null);
  const [availableCameras, setAvailableCameras] = useState<any[]>([]);

  const fetchTodayDogsCount = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Admin token bulunamadı');
      }

      const headers = getAuthHeaders(token);
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.TODAY_DOGS}`, {
        method: 'GET',
        headers,
      });

      const data = await response.json();
      
      if (data.success) {
        setTodayDogsCount(data.total || 0);
      }
    } catch (error) {
      setTodayDogsCount(0);
    }
  };

  const fetchOteldekiKopekler = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Admin token bulunamadı');
      }

      const headers = getAuthHeaders(token);
      // Aktif (check-out yapılmamış) köpekleri getir
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CHECKIN_ACTIVE_STAFF}`, {
        method: 'GET',
        headers,
      });

      const data = await response.json();
      
      if (data.success) {
        // API'den gelen veriyi Kopek interface'ine uyarlayalım
        const activeDogs: Kopek[] = data.data.map((checkin: any) => {
          return {
            _id: checkin.id,
            name: checkin.name,
            breed: checkin.breed,
            gender: checkin.gender || '',
            birthDate: checkin.birthDate || '',
            age: checkin.age || '',
            weight: checkin.weight,
            size: checkin.size || '',
            color: checkin.color || '',
            profileImage: checkin.profileImage,
            description: checkin.description || '',
            isSpayed: checkin.isSpayed,
            owner: {
              _id: checkin.ownerId || '',
              name: checkin.ownerName,
              email: checkin.ownerEmail,
              phone: checkin.ownerPhone,
            },
            isActive: true,
            ownerNotes: checkin.ownerNotes || '',
            staffNotes: checkin.staffNotes || '',
            checkinInfo: {
              id: checkin.checkinId,
              checkInTime: checkin.checkInTime,
              lastUpdateTime: checkin.lastUpdateTime,
              branch: {
                _id: checkin.branchId || '',
                name: checkin.branchName,
                code: checkin.branchCode || '',
              },
              staff: {
                _id: checkin.staffId || '',
                name: checkin.staffName,
                email: checkin.staffEmail,
                phone: checkin.staffPhone,
                position: checkin.staffPosition,
              }
            }
          };
        });
        
        setKopekler(activeDogs);
      } else {
        throw new Error(data.message || 'Oteldeki Kedi/Köpekler listelenemedi');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bilinmeyen hata');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOteldekiKopekler();
    fetchTodayDogsCount();
  }, []);

  // Filtreleme
  const filteredKopekler = kopekler.filter(kopek => {
    const matchesSearch = kopek.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (kopek.owner && kopek.owner.name && kopek.owner.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (kopek.owner && kopek.owner.surname && kopek.owner.surname.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         kopek.breed.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBreed = filterBreed === '' || kopek.breed === filterBreed;
    return matchesSearch && matchesBreed;
  });

  // Unique breeds for filter
  const uniqueBreeds = Array.from(new Set(kopekler.map(k => k.breed))).filter(Boolean);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateAge = (kopek: Kopek) => {
    // Önce backend'den gelen age alanını kontrol et
    if (kopek.age && kopek.age.trim() !== '') {
      return kopek.age;
    }
    
    // Eğer age yoksa birthDate'den hesapla
    const birthDate = kopek.birthDate;
    
    // Boş veya null kontrolü
    if (!birthDate || birthDate === null || birthDate === undefined || birthDate === '') {
      return 'Bilinmiyor';
    }
    
    // String ise trim yap
    const cleanBirthDate = typeof birthDate === 'string' ? birthDate.trim() : birthDate;
    if (cleanBirthDate === '') {
      return 'Bilinmiyor';
    }
    
    const today = new Date();
    let birth: Date;
    
    try {
      // Farklı tarih formatlarını dene
      if (typeof cleanBirthDate === 'string') {
        // ISO string formatı
        if (cleanBirthDate.includes('T') || cleanBirthDate.includes('Z')) {
          birth = new Date(cleanBirthDate);
        }
        // YYYY-MM-DD formatı
        else if (cleanBirthDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
          birth = new Date(cleanBirthDate + 'T00:00:00');
        }
        // DD/MM/YYYY formatı
        else if (cleanBirthDate.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
          const [day, month, year] = cleanBirthDate.split('/');
          birth = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
        // MM/DD/YYYY formatı
        else if (cleanBirthDate.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
          const [month, day, year] = cleanBirthDate.split('/');
          birth = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
        // Diğer formatlar
        else {
          birth = new Date(cleanBirthDate);
        }
      } else {
        birth = new Date(cleanBirthDate);
      }
      
      // Geçersiz tarih kontrolü
      if (isNaN(birth.getTime())) {
        return 'Geçersiz tarih';
      }
      
      // Gelecek tarih kontrolü
      if (birth > today) {
        return 'Gelecek tarih';
      }
      
      // Çok eski tarih kontrolü (100 yaşından büyük)
      const hundredYearsAgo = new Date();
      hundredYearsAgo.setFullYear(hundredYearsAgo.getFullYear() - 100);
      if (birth < hundredYearsAgo) {
        return 'Geçersiz tarih';
      }
      
      let years = today.getFullYear() - birth.getFullYear();
      let months = today.getMonth() - birth.getMonth();
      
      if (months < 0) {
        years--;
        months += 12;
      }
      
      let ageResult;
      if (years === 0) {
        ageResult = months === 1 ? '1 ay' : `${months} ay`;
      } else if (months === 0) {
        ageResult = years === 1 ? '1 yaş' : `${years} yaş`;
      } else {
        ageResult = `${years} yaş ${months} ay`;
      }
      
      return ageResult;
      
    } catch (error) {
      return 'Geçersiz tarih';
    }
  };

  const handleCheckout = async (kopekId: string, checkinId: string, kopekName: string) => {
    // Onay dialogu için veriyi ayarla
    setConfirmData({ kopekId, checkinId, kopekName });
    setShowConfirmDialog(true);
  };

  const confirmCheckout = async () => {
    if (!confirmData) return;

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Admin token bulunamadı');
      }

      const headers = getAuthHeaders(token);
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CHECKIN_CHECKOUT.replace('{id}', confirmData.checkinId)}`, {
        method: 'PATCH',
        headers,
      });

      const data = await response.json();
      
      if (data.success) {
        // Başarılı check-out sonrası listeyi yenile
        await fetchOteldekiKopekler();
        setError(null);
        setSuccessMessage(`${confirmData.kopekName} için check-out işlemi başarıyla tamamlandı!`);
        // 3 saniye sonra başarı mesajını temizle
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(data.message || 'Check-out işlemi başarısız');
      }
    } catch (error) {
      setError(`Check-out hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
      setSuccessMessage(null);
    } finally {
      // Dialog'u kapat
      setShowConfirmDialog(false);
      setConfirmData(null);
    }
  };

  const cancelCheckout = () => {
    setShowConfirmDialog(false);
    setConfirmData(null);
  };

  // Düzenle fonksiyonları
  const handleEdit = async (kopekId: string, checkinId: string, kopekName: string) => {
    try {
      // Checkin detaylarını getir
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Admin token bulunamadı');
      }

      const headers = getAuthHeaders(token);
      
      // Checkin detaylarını ve kameraları paralel olarak getir
      const [checkinResponse, camerasResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/checkin/${checkinId}`, {
          method: 'GET',
          headers,
        }),
        fetch(`${API_BASE_URL}/api/staff/cameras`, {
          method: 'GET',
          headers,
        })
      ]);

      const checkinData = await checkinResponse.json();
      const camerasData = await camerasResponse.json();
      
      if (checkinData.success) {
        const checkin = checkinData.data;
        setEditData({
          kopekId,
          checkinId,
          kopekName,
          staffNotes: checkin.notes || '',
          ownerNotes: checkin.ownerNotes || '',
          selectedCameras: checkin.selectedCameras?.map((cam: any) => cam.cameraId) || []
        });
        
        // Kameraları set et
        if (camerasData.success) {
          setAvailableCameras(camerasData.data || []);
        }
        
        setShowEditDialog(true);
      } else {
        throw new Error(checkinData.message || 'Checkin detayları getirilemedi');
      }
    } catch (error) {
      setError(`Düzenleme hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
    }
  };

  const saveEdit = async () => {
    if (!editData) return;

    try {
      // Save edit started
      
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('Admin token bulunamadı');
      }

      const requestBody = {
        notes: editData.staffNotes,
        ownerNotes: editData.ownerNotes,
        selectedCameras: editData.selectedCameras.map(cameraId => {
          // Available cameras'dan cameraUrl'i bul
          const camera = availableCameras.find(cam => cam.cameraId === cameraId);
          return {
            cameraId,
            cameraName: camera?.cameraName || `Kamera ${cameraId}`,
            cameraUrl: camera?.cameraUrl || `http://16.171.151.193/barkleyclub/${cameraId}.m3u8`,
            isActive: true
          };
        })
      };
      
      // Making API request

      const headers = getAuthHeaders(token);
      const response = await fetch(`${API_BASE_URL}/api/checkin/${editData.checkinId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(`${editData.kopekName} için düzenleme başarıyla tamamlandı!`);
        setTimeout(() => setSuccessMessage(null), 3000);
        setShowEditDialog(false);
        setEditData(null);
        await fetchOteldekiKopekler(); // Listeyi yenile
      } else {
        throw new Error(data.message || 'Düzenleme başarısız');
      }
    } catch (error) {
      setError(`Kaydetme hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
    }
  };

  const cancelEdit = () => {
    setShowEditDialog(false);
    setEditData(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">🐕 Oteldeki Kedi/Köpekler</h1>
            <p className="text-gray-600 mt-2 font-medium">Şu anda aktif olan (check-out yapılmamış) köpeklerin listesi</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-md p-6 border-2 border-gray-200 hover:shadow-lg transition-all hover:border-orange-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-gray-600 uppercase tracking-wide">Oteldeki Köpek</p>
              <p className="text-4xl font-black text-gray-900 mt-2">{kopekler.length}</p>
            </div>
            <div className="bg-gradient-to-br from-orange-100 to-amber-100 p-3 rounded-xl">
              <Dog size={32} className="text-orange-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6 border-2 border-gray-200 hover:shadow-lg transition-all hover:border-orange-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-gray-600 uppercase tracking-wide">Farklı Cins</p>
              <p className="text-4xl font-black text-gray-900 mt-2">{uniqueBreeds.length}</p>
            </div>
            <div className="bg-gradient-to-br from-orange-100 to-amber-100 p-3 rounded-xl">
              <Filter size={32} className="text-orange-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6 border-2 border-gray-200 hover:shadow-lg transition-all hover:border-orange-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-gray-600 uppercase tracking-wide">Bugün Gelen</p>
              <p className="text-4xl font-black text-gray-900 mt-2">{todayDogsCount}</p>
            </div>
            <div className="bg-gradient-to-br from-orange-100 to-amber-100 p-3 rounded-xl">
              <Calendar size={32} className="text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md border-2 border-gray-200 p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <div className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-gray-100 p-2 rounded-lg">
              <Search className="text-gray-600" size={16} />
            </div>
            <input
              type="text"
              placeholder="Köpek adı, sahip adı veya cins ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-14 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 bg-gray-50 focus:bg-white font-medium transition-all shadow-sm focus:shadow-md"
            />
          </div>
          <div className="relative group">
            <div className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-gray-100 p-2.5 rounded-lg pointer-events-none z-10 shadow-sm">
              <Filter className="text-gray-600" size={18} />
            </div>
            <select
              value={filterBreed}
              onChange={(e) => setFilterBreed(e.target.value)}
              className="w-full pl-16 pr-14 py-3.5 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 bg-gray-50 focus:bg-white font-bold text-gray-800 transition-all shadow-sm focus:shadow-lg hover:border-orange-300 hover:shadow-md appearance-none cursor-pointer text-sm"
            >
              <option value="">🐕 Tüm Cinsler</option>
              {uniqueBreeds.map((breed) => (
                <option key={breed} value={breed}>{breed}</option>
              ))}
            </select>
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <div className="bg-gradient-to-br from-orange-400 to-amber-500 p-2 rounded-lg shadow-sm">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center bg-gradient-to-r from-orange-400 to-amber-500 rounded-xl px-4 py-3 shadow-md">
            <span className="text-sm font-bold text-white">
              {filteredKopekler.length} / {kopekler.length} Köpek
            </span>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          <strong>Hata:</strong> {error}
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md">
          <strong>Başarılı:</strong> {successMessage}
        </div>
      )}

      {/* Dogs List - Card Layout */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Köpekler yükleniyor...</p>
        </div>
      ) : filteredKopekler.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Dog size={32} className="text-gray-400" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Köpek Bulunamadı</h3>
          <p className="text-gray-600 font-medium">
            {searchTerm || filterBreed ? 'Arama kriterlerinize uygun köpek bulunamadı.' : 'Şu anda otelde köpek bulunmuyor.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredKopekler.map((kopek) => (
            <div key={kopek._id} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {kopek.profileImage && !kopek.profileImage.startsWith('file://') ? (
                    <img 
                      src={kopek.profileImage} 
                      alt={kopek.name}
                      className="w-12 h-12 rounded-full object-cover ring-2 ring-white"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <div className={`w-12 h-12 bg-white/30 rounded-full flex items-center justify-center text-white font-bold text-lg ring-2 ring-white ${kopek.profileImage && !kopek.profileImage.startsWith('file://') ? 'hidden' : ''}`}>
                    {kopek.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">{kopek.name}</h3>
                    <p className="text-orange-100 text-sm">{kopek.breed} • {kopek.color || 'Renk belirtilmemiş'}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => handleEdit(kopek._id, kopek.checkinInfo?.id || '', kopek.name)}
                    className="bg-white text-orange-600 px-3 py-1.5 rounded-md hover:bg-orange-50 transition-colors text-sm font-medium flex items-center"
                  >
                    <Edit size={14} className="mr-1" />
                    Düzenle
                  </button>
                  <button 
                    onClick={() => handleCheckout(kopek._id, kopek.checkinInfo?.id || '', kopek.name)}
                    className="bg-red-500 text-white px-3 py-1.5 rounded-md hover:bg-red-600 transition-colors text-sm font-medium"
                  >
                    Çıkış Yap
                  </button>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  
                  {/* Sahip Bilgileri - GENIŞ */}
                  <div className="lg:col-span-2 bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="text-sm font-bold text-gray-700 uppercase mb-3 flex items-center">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                      👤 Sahip Bilgileri
                    </h4>
                    <div className="space-y-2.5">
                      <ProtectedData
                        label="Sahip Adı"
                        value={kopek.owner ? `${kopek.owner.name || ''} ${kopek.owner.surname || ''}`.trim() : 'Silinen Kullanıcı'}
                        type="name"
                      />
                      <ProtectedData
                        label="Telefon"
                        value={kopek.owner ? kopek.owner.phone : 'Hesap silindi'}
                        type="phone"
                        dogId={kopek._id}
                        ownerId={kopek.owner ? kopek.owner._id : undefined}
                        pageContext="oteldeki-kopekler"
                      />
                      <ProtectedData
                        label="E-posta"
                        value={kopek.owner ? kopek.owner.email : 'Hesap silindi'}
                        type="email"
                        dogId={kopek._id}
                        ownerId={kopek.owner ? kopek.owner._id : undefined}
                        pageContext="oteldeki-kopekler"
                      />
                    </div>
                  </div>

                  {/* Köpek Özellikleri - Cins Dahil */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 flex flex-col">
                    <h4 className="text-sm font-bold text-gray-700 uppercase mb-3 flex items-center">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                      🐕 Özellikler
                    </h4>
                    <div className="space-y-2 flex-grow">
                      <div className="bg-white rounded-md px-3 py-2 border border-gray-300">
                        <div className="text-xs text-gray-500 font-medium mb-0.5">Cins</div>
                        <div className="text-sm text-gray-900 font-bold">{kopek.breed}</div>
                      </div>
                      <div className="bg-white rounded-md px-3 py-2 border border-gray-300">
                        <div className="text-xs text-gray-500 font-medium mb-0.5">Cinsiyet</div>
                        <div className="text-sm text-gray-900 font-bold">{kopek.gender || '-'}</div>
                      </div>
                      <div className="bg-white rounded-md px-3 py-2 border border-gray-300">
                        <div className="text-xs text-gray-500 font-medium mb-0.5">Yaş</div>
                        <div className="text-sm text-gray-900 font-bold">{calculateAge(kopek)}</div>
                      </div>
                      <div className="bg-white rounded-md px-3 py-2 border border-gray-300">
                        <div className="text-xs text-gray-500 font-medium mb-0.5">Kısırlık</div>
                        <div className="text-sm text-gray-900 font-bold">{kopek.isSpayed === true ? 'Kısır ✓' : kopek.isSpayed === false ? 'Kısır Değil ✗' : '-'}</div>
                      </div>
                      <div className="bg-white rounded-md px-3 py-2 border border-gray-300">
                        <div className="text-xs text-gray-500 font-medium mb-0.5">Kilo</div>
                        <div className="text-sm text-gray-900 font-bold">{kopek.weight ? `${kopek.weight} kg` : '-'}</div>
                      </div>
                    </div>
                  </div>

                  {/* Check-in & Personel Bilgileri - Birleştirildi */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 flex flex-col">
                    <h4 className="text-sm font-bold text-gray-700 uppercase mb-3 flex items-center">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                      📅 Otel Bilgileri
                    </h4>
                    <div className="space-y-2 flex-grow">
                      <div className="bg-white rounded-md px-3 py-2 border border-gray-300">
                        <div className="text-xs text-gray-500 font-medium mb-0.5">Check-in Tarihi</div>
                        <div className="flex items-center text-sm text-gray-900 font-bold">
                          <Calendar size={12} className="mr-1.5 text-orange-500" />
                          {formatDate(kopek.checkinInfo?.checkInTime || '')}
                        </div>
                      </div>
                      
                      {kopek.checkinInfo?.lastUpdateTime && (
                        <div className="bg-white rounded-md px-3 py-2 border border-gray-300">
                          <div className="text-xs text-gray-500 font-medium mb-0.5">Son Güncelleme</div>
                          <div className="text-xs text-gray-900 font-bold">{formatDate(kopek.checkinInfo.lastUpdateTime)}</div>
                        </div>
                      )}
                      
                      <div className="bg-white rounded-md px-3 py-2 border border-gray-300">
                        <div className="text-xs text-gray-500 font-medium mb-0.5">Şube</div>
                        <div className="text-sm text-gray-900 font-bold">{kopek.checkinInfo?.branch.name}</div>
                      </div>
                      
                      <div className="bg-white rounded-md px-3 py-2 border border-gray-300">
                        <div className="text-xs text-gray-500 font-medium mb-0.5">Personel</div>
                        <div className="text-sm text-gray-900 font-bold">{kopek.checkinInfo?.staff.name}</div>
                        <div className="text-xs text-gray-600 font-medium mt-0.5">{kopek.checkinInfo?.staff.position}</div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          ))}
        </div>
      )}

       {/* Onay Dialogu */}
      {showConfirmDialog && confirmData && (
        <div
          className="fixed inset-0 w-screen h-screen bg-black/30 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 10000, top: '-30px' }}
        >
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center shadow-md">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white">Check-out Onayı</h3>
              </div>
              <button
                onClick={cancelCheckout}
                className="text-white/70 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-all"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-5">
              <p className="text-gray-700 text-base leading-relaxed">
                <strong className="text-gray-900">{confirmData.kopekName}</strong> için check-out işlemini gerçekleştirmek istediğinizden emin misiniz?
              </p>

              <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 text-sm text-orange-800">
                Bu işlem tamamlandığında köpek otel listesinden kaldırılacak ve ziyaret kaydı güncellenecek.
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  onClick={cancelCheckout}
                  className="px-6 py-3 text-gray-700 border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all font-bold"
                >
                  İptal
                </button>
                <button
                  onClick={confirmCheckout}
                  className="px-8 py-3 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-xl hover:from-orange-600 hover:to-amber-700 transition-all shadow-md hover:shadow-lg font-bold"
                >
                  Çıkış Yap
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

       {/* Düzenle Dialogu */}
        {showEditDialog && editData && (
          <div
            className="fixed inset-0 w-screen h-screen bg-black/30 backdrop-blur-sm flex items-center justify-center p-4"
            style={{ zIndex: 9999, top: '-30px' }}
          >
           <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] shadow-2xl overflow-hidden">
             {/* Header */}
             <div className="bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-4 flex items-center justify-between">
               <div className="flex items-center space-x-3">
                 <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center shadow-md">
                   <Edit className="w-6 h-6 text-white" />
                 </div>
                 <h3 className="text-xl font-bold text-white">
                   {editData.kopekName} - Düzenle
                 </h3>
               </div>
              <button
                onClick={cancelEdit}
                className="text-white/70 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-all relative"
                style={{ top: '-30px' }}
              >
                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                 </svg>
               </button>
             </div>
             
             {/* Body - Scrollable */}
             <div className="overflow-y-auto max-h-[calc(90vh-180px)]">
               <div className="p-6 space-y-5">
                 {/* Staff Notları */}
                 <div>
                   <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center">
                     <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                     Staff Notları (Giriş Sırasında)
                   </label>
                   <textarea
                     value={editData.staffNotes}
                     onChange={(e) => setEditData({...editData, staffNotes: e.target.value})}
                     className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 bg-gray-50 focus:bg-white transition-all font-medium resize-none"
                     rows={3}
                     placeholder="Staff tarafından giriş sırasında eklenen notlar..."
                   />
                 </div>

                 {/* Sahip Notları */}
                 <div>
                   <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center">
                     <span className="w-2 h-2 bg-gray-500 rounded-full mr-2"></span>
                     Sahip Notları (Salt Okunur)
                   </label>
                   <div className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl bg-gray-50 text-gray-700 min-h-[80px] font-medium break-words overflow-wrap-anywhere">
                     {editData.ownerNotes || "Sahibinden gelen özel notlar bulunmuyor..."}
                   </div>
                 </div>

                 {/* Kamera Seçimi */}
                 <div>
                   <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center">
                     <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                     İzin Verilen Kameralar
                   </label>
                   <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                     {availableCameras.map((camera) => (
                       <label key={camera.cameraId} className="flex items-center space-x-2 p-3 border-2 border-gray-200 rounded-xl hover:border-orange-300 hover:bg-orange-50 transition-all cursor-pointer">
                         <input
                           type="checkbox"
                           checked={editData.selectedCameras.includes(camera.cameraId)}
                           onChange={(e) => {
                             if (e.target.checked) {
                               setEditData({
                                 ...editData,
                                 selectedCameras: [...editData.selectedCameras, camera.cameraId]
                               });
                             } else {
                               setEditData({
                                 ...editData,
                                 selectedCameras: editData.selectedCameras.filter(id => id !== camera.cameraId)
                               });
                             }
                           }}
                           className="rounded-md border-gray-300 text-orange-500 focus:ring-orange-400 w-5 h-5"
                         />
                         <span className="text-sm font-medium text-gray-700">{camera.cameraName}</span>
                       </label>
                     ))}
                   </div>
                   {availableCameras.length === 0 && (
                     <p className="text-sm font-medium text-gray-500 bg-gray-50 rounded-lg p-3 text-center border border-gray-200">Kamera bilgisi yükleniyor...</p>
                   )}
                 </div>
               </div>
             </div>
             
             {/* Footer - Sticky */}
             <div className="bg-white border-t-2 border-gray-100 px-6 py-4 flex justify-end space-x-3">
               <button
                 onClick={cancelEdit}
                 className="px-6 py-3 text-gray-700 border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all font-bold"
               >
                 İptal
               </button>
               <button
                 onClick={saveEdit}
                 className="px-8 py-3 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-xl hover:from-orange-600 hover:to-amber-700 transition-all shadow-md hover:shadow-lg font-bold"
               >
                 Kaydet
               </button>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };

export default OteldekiKopekler;
